import React, { useState, useRef, useEffect } from "react";
import { Button } from "../ui/button";
import { Copy } from "lucide-react"; // Removed Box, Code, Wind
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useCanvasId } from "@/utils/utils"; // Assuming canvasId is needed for the command
import { useOpenDeepLinkForEditor } from "@/utils/localEditingUtils";
import { IconArrowTopRight } from "../ui/icons";

interface Props {}

const ExtensionSection = () => {
  const [isNarrow, setIsNarrow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const verticalIconsThreshold = 220; // px

  useEffect(() => {
    const containerElement = containerRef.current;
    if (!containerElement) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setIsNarrow(entry.contentRect.width < verticalIconsThreshold);
      }
    });

    observer.observe(containerElement);
    setIsNarrow(containerElement.offsetWidth < verticalIconsThreshold);

    return () => {
      observer.disconnect();
    };
  }, []);

  const openDeepLinkForEditor = useOpenDeepLinkForEditor();

  return (
    <>
      <div className="flex flex-col gap-2 border-b p-4">
        <h3 className="font-medium text-sm">VSCode IDE Extension</h3>
      </div>
      <div ref={containerRef} className="flex flex-col gap-2 border-b p-4">
        <p className="text-sm">
          Install our VSCode IDE extension to connect your canvas to your
          favourite editors.
        </p>
        <Button
          variant="primary"
          className="w-full h-8 gap-1 items-center text-xs my-2"
          onClick={() =>
            window.open(
              "https://marketplace.visualstudio.com/items?itemName=TempoLabs.tempo",
              "_blank",
            )
          }
        >
          Install <IconArrowTopRight className="w-4 h-4" />
        </Button>
        <p className="text-sm mt-2">Then, choose your editor below:</p>
        <div
          className={cn(
            "grid gap-2 mt-1",
            isNarrow ? "grid-cols-1" : "grid-cols-3",
          )}
        >
          {/* Editor Buttons */}
          <Button
            variant="secondary"
            className="h-16 flex flex-col items-center justify-center gap-1.5 p-2 text-xs border-none hover:bg-background-tertiary"
            onClick={() => openDeepLinkForEditor("cursor")}
          >
            <img src="/images/cursor-icon.png" className="w-5 h-5" />
            Cursor
          </Button>
          <Button
            variant="secondary"
            className="h-16 flex flex-col items-center justify-center gap-1.5 p-2 text-xs border-none hover:bg-background-tertiary"
            onClick={() => openDeepLinkForEditor("vscode")}
          >
            <img src="/images/vscode-icon.svg" className="w-5 h-5" />
            VSCode
          </Button>
          <Button
            variant="secondary"
            className="h-16 flex flex-col items-center justify-center gap-1.5 p-2 text-xs border-none hover:bg-background-tertiary"
            onClick={() => openDeepLinkForEditor("windsurf")}
          >
            <img src="/images/windsurf-icon.svg" className="w-5 h-5" />
            Windsurf
          </Button>
        </div>
      </div>
    </>
  );
};

const LocalModeSection = () => {
  const [copied, setCopied] = useState(false);
  const canvasId = useCanvasId() || "c8a52a5...";
  const command = `npx tempo-ai connect ${canvasId}`;

  const copyCommand = () => {
    if (import.meta.env.VITE_ENVIRONMENT === "DEV") {
      navigator.clipboard.writeText(`TEMPO_ENV="DEV" ${command}`);
    } else if (import.meta.env.VITE_ENVIRONMENT === "STAGING") {
      navigator.clipboard.writeText(`TEMPO_ENV="STAGING" ${command}`);
    } else {
      navigator.clipboard.writeText(command);
    }

    setCopied(true);
    toast.success("Command copied to clipboard");
    setTimeout(() => {
      setCopied(false);
    }, 1500);
  };

  return (
    <div className="flex flex-col gap-2 p-4 border-b">
      <h3 className="font-medium text-sm">Want to code locally?</h3>
      <p className="text-sm">
        Run this command in your terminal to copy files to your local dev setup.
      </p>
      <div className="bg-background-secondary px-3 py-2.5 rounded-md flex items-center justify-between mt-1">
        <div className="text-xs font-mono flex-1 whitespace-nowrap overflow-x-auto hide-scrollbar flex items-center">
          <span className="text-text-secondary mr-2">{">"}</span>
          <span className="text-text-primary">{command}</span>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 p-0.5 text-text-secondary hover:text-text-primary hover:bg-background-tertiary shrink-0 ml-2"
          onClick={copyCommand}
          disabled={copied}
          aria-label="Copy command"
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
};

const LocalEditing: React.FunctionComponent<Props> = React.memo((props) => {
  return (
    <div className="h-full text-white">
      <ExtensionSection />
      <LocalModeSection />
    </div>
  );
});

export default LocalEditing;
